import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Telr's return page for APP payments — a hand-off, nothing more.
//
// The app used to give Telr `maabar://payment-return` as the return URL, and
// app payments ended on a Telr page reading "INVALID". CONFIRMED 2026-09-28 by
// Telr's own reason in the telr-create log: "E01:Invalid request", note
// "Return URL details not valid" (exception 3890915) — Telr rejects a custom
// scheme as a return URL. The web, on the same store, sends https
// (/telr-return) and works. So the app now
// sends https://maabar.io/telr-app-return?… and this page immediately forwards
// the SAME query string into the app — maabar://payment-return?… — which is
// what the app's in-app browser (expo-web-browser openAuthSessionAsync) waits
// for. The browser closes on that redirect and the app calls telr-verify with
// the order ref it already holds.
//
// DELIBERATELY DUMB: it verifies nothing, reads nothing, writes nothing, and
// needs no session. Verification stays in the app → telr-verify, exactly as
// before. A declined or cancelled payment lands here too; telr-verify then
// reports it as not paid. The query string is passed through untouched and only
// ever into our own scheme — never into a web URL — so the page cannot be used
// to send anyone elsewhere.
//
// The visible button is the fallback for a browser that does not follow the
// automatic redirect (some in-app browsers need a user gesture for a custom
// scheme).

const APP_RETURN = 'maabar://payment-return';

const T = {
  ar: { title: 'جارٍ إعادتك إلى تطبيق مَعبر…', body: 'إذا لم يُفتح التطبيق تلقائياً، اضغط الزر.', cta: 'العودة إلى تطبيق مَعبر' },
  en: { title: 'Taking you back to the Maabar app…', body: "If the app doesn't open by itself, tap the button.", cta: 'Return to the Maabar app' },
  zh: { title: '正在返回 Maabar 应用…', body: '如果应用没有自动打开，请点击按钮。', cta: '返回 Maabar 应用' },
};

export default function TelrAppReturn({ lang = 'ar' }) {
  const { search } = useLocation();
  const deepLink = useMemo(() => `${APP_RETURN}${search || ''}`, [search]);
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';

  useEffect(() => {
    // replace(), not assign(): this page should not stay in history behind the app.
    try { window.location.replace(deepLink); } catch (e) {
      console.error('[TelrAppReturn] redirect into the app failed:', e?.message || e);
    }
  }, [deepLink]);

  return (
    <div style={{ minHeight: 'var(--app-dvh)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', padding: 24 }}>
      <div dir={isAr ? 'rtl' : 'ltr'} style={{ maxWidth: 420, width: '100%', textAlign: 'center', background: 'var(--bg-overlay)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px 28px' }}>
        <h1 style={{ fontSize: 20, fontWeight: 400, color: 'var(--text-primary)', margin: '0 0 10px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.title}</h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 22px', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{t.body}</p>
        <a href={deepLink} className="btn-primary"
          style={{ display: 'inline-block', minHeight: 44, lineHeight: '44px', padding: '0 22px', borderRadius: 'var(--radius-md)', textDecoration: 'none', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
          {t.cta}
        </a>
      </div>
    </div>
  );
}
